/**
 * @author Radek Liška <ing.radek.liska@gmail.com>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

// @ts-check

/* eslint-env browser */

import isNil from 'lodash/isNil';
import { defineComponent } from 'vue';

// @ts-ignore Není to ESM.
import template from './setup-root.html';

import config from '../../config';
import { $root } from '../../player/player';
import { mixinType } from '../../util.type';
import ControlButton from '../control/control-button';
import ErrorName from '../error/error-name';
import LoaderContainer from '../loader/loader-container';
import StringUtil from '../string/string-util';
import SetupFailure from './setup-failure';
import * as SetupManager from './setup-manager';
import SetupManual from './setup-manual';
import SetupOrganization from './setup-organization';
import SetupPanelLeft from './setup-panel-left';
import SetupPanelRight from './setup-panel-right';
import SetupQr from './setup-qr';
import SetupSettings from './setup-settings';
import SetupStorage from './setup-storage';
import SetupSuccess from './setup-success';

/**
 * @import { ServerInfo } from '../server'
 * @import { VirtualKeyboardInfo } from '../virtual-keyboard'
 * @import { Organization } from './setup-manager'
 */

// TODO Univerzální komponenta `FormField`. Nyní se kód opakuje.
// TODO Automatická synchronizace v GUI.
// - Mělo by provádění sync být opravdu tak agresivní? Zařízení již pak nelze jednoduše odinstalovat.
// - Co když bude uživatel chtít zařízení zaregistrovat na jiném serveru? Sync ihned spáruje výchozí server.
// - Detekci vždy nechat potvrdit v GUI.
// - Co třeba tlačítko "Automatic device setup", které by signalizovalo načítání / úspěch / neúspěch.
// TODO Panely na menších rozlišeních překrývají obsah.
// - Zatím se neřešilo, protože není poptávka po takto malých zařízeních.
// - V ideálním případě by se panely měli poskládat pod sebe a za nimi obsah.
export default defineComponent({
  name: 'SetupRoot',
  template,
  components: {
    LoaderContainer,
    SetupFailure,
    SetupManual,
    SetupOrganization,
    SetupPanelLeft,
    SetupPanelRight,
    SetupQr,
    SetupSettings,
    SetupStorage,
    SetupSuccess
  },
  created() {
    const {
      controlManager,
      focusManager,
      networkListener,
      serverConnectionMonitor,
      virtualKeyboardManager
    } = $root(this);
    this.automaticEnabled = false;
    this.automaticTimeout = undefined;
    this.deviceId = null;
    this.organization = null;
    // Sledování ovládacích tlačítek.
    controlManager.on('buttonDown', this.onButtonDown);
    // Spuštění podpory pro focus.
    focusManager.start();
    // Sledování připojení k síti.
    networkListener.on('onlineChanged', this.onOnlineChanged);
    // Načte se výchozí serverUrl z konfigurace.
    serverConnectionMonitor.setServerUrl(config.setup.serverUrl);
    // Sledování připojení k serveru.
    serverConnectionMonitor.on(
      'connectionChanged',
      this.onServerConnectionChanged
    );
    // Sledování virtuální klávesnice.
    virtualKeyboardManager.on('infoChanged', this.onVirtualKeyboardChanged);
    // Inicializace stavu virtuální klávesnice.
    virtualKeyboardManager
      .getInfo()
      .then((info) => {
        this.rootStyle = this.getRootStyle(info);
      })
      // Spustí automatickou registraci se serverem na základě otisku.
      .then(() => this.startAutomatic())
      .catch((error) => this.handleError(error));
  },
  data() {
    const { fingerprint, networkListener, updateUrlConfig } = $root(this);
    let { serverUrl } = config.setup;
    if (updateUrlConfig?.serverUrl) {
      serverUrl = updateUrlConfig.serverUrl;
    }

    const { storage } = config;
    return mixinType(
      {
        defaultServerUrl: serverUrl,
        defaultStorage: storage,
        deviceName: config.setup.deviceName,
        // Pokud je uživatelsky zadána serverUrl,
        // pak je zakázaná editace pomocí inputboxu.
        /** @type {boolean} */
        editableServerUrl: !updateUrlConfig?.serverUrl,
        /** @type {string | null} */
        failureMessage: null,
        /** @type {number | null} */
        failureRetryDelay: null,
        /** @type {number | null} */
        failureRetryTime: null,
        fingerprint,
        isOnline: networkListener.isOnline(),
        /** @type {Organization[]} */
        organizationArray: [],
        page: 'qr',
        password: config.setup.password,
        /** @type {Record<string, any> | null} TODO #555 */
        rootStyle: null,
        serverUrl,
        storage,
        /** @type {number | null} */
        successTime: null,
        serverConnected: false,
        username: config.setup.username
      },
      /**
       * @type {{
       *   automaticEnabled: boolean;
       *   automaticTimeout: ReturnType<typeof setTimeout> | undefined;
       *   deviceId: string | null;
       *   organization: Organization | null;
       *   submitted: boolean;
       * }}
       */
      ({})
    );
  },
  destroyed() {
    const {
      controlManager,
      focusManager,
      networkListener,
      serverConnectionMonitor,
      virtualKeyboardManager
    } = $root(this);
    this.stopAutomatic();
    controlManager.off('buttonDown', this.onButtonDown);
    focusManager.stop();
    networkListener.off('onlineChanged', this.onOnlineChanged);
    serverConnectionMonitor.off(
      'connectionChanged',
      this.onServerConnectionChanged
    );
    virtualKeyboardManager.off('infoChanged', this.onVirtualKeyboardChanged);
  },
  emits: {
    finish() {}
  },
  methods: {
    back() {
      // TODO Šlo by zpracovat lépe.
      // - Mohli by sledovat jednotlivé stránky a vyvolávat "back".
      // - Obecně by pomohlo nasazení routeru.
      // - Také by se neměly vyvolávat on* metody.
      switch (this.page) {
        case 'failure':
          return this.onFailureBack();
        case 'manual':
          return this.onManualBack();
        case 'organization':
          return this.onOrganizationBack();
        case 'settings':
          return this.onSettingsBack();
        default:
          // Výchozí operací je vypnutí aplikace.
          return $root(this).appManager.exit();
      }
    },
    /**
     * @param {VirtualKeyboardInfo} info
     * @returns {Record<string, any> | null} TODO #555
     */
    getRootStyle(info) {
      if (info.isOverlapping()) {
        // Pokud klávesnice překrývá obsah, vytvoří se místo pro její zobrazení.
        return { paddingBottom: info.getHeight() };
      }
      // Bez virtuální klávesnice se zarovnání na výšku neupravuje.
      return null;
    },
    /**
     * @param {Error} error
     */
    handleError(error) {
      $root(this).logger.error(error);
    },
    // TODO #626: Refaktorovat na ServerDeviceId.
    /**
     * @param {string} deviceId
     * @param {string} serverUrl
     * @param {string} fingerprint
     * @returns {Promise<void>}
     */
    async install(deviceId, serverUrl, fingerprint) {
      const { storage } = this;
      await $root(this).store.updateSession($root(this).session.id, {
        deviceId,
        serverUrl,
        fingerprint,
        storage
      });
      this.showSuccess();
    },
    onAutomatic() {
      // Provede se pokus o registraci.
      new Promise((resolve) => resolve(this.registerAutomatic()))
        .catch((error) => this.handleError(error))
        // Pokud nebyla registrace provedena, tak se po zpoždění provede další pokus.
        .finally(() => {
          if (this.automaticEnabled) {
            this.automaticTimeout = setTimeout(
              this.onAutomatic,
              config.setup.automaticDelay
            );
          }
        });
    },
    /**
     * @param {ControlButton} controlButton
     */
    onButtonDown(controlButton) {
      if (controlButton === ControlButton.Back) {
        this.back();
      }
    },
    onFailureBack() {
      this.showManual();
    },
    onFailureRetry() {
      new Promise((resolve) => {
        const { failureDelayMultiplier, failureDelayMax } = config.setup;
        this.failureRetryDelay = Math.min(
          (this.failureRetryDelay ?? config.setup.failureDelayBase) *
            failureDelayMultiplier,
          failureDelayMax
        );
        this.showLoader();
        const { organization } = this;
        if (!isNil(organization)) {
          return resolve(
            this.registerManualComplete(
              this.serverUrl,
              this.username,
              this.password,
              organization.id,
              this.deviceName
            )
          );
        }
        return resolve(
          this.registerManualSimple(
            this.serverUrl,
            this.username,
            this.password,
            this.deviceName
          )
        );
      }).catch((error) => this.showFailure(error));
    },
    onFinish() {
      this.$emit('finish');
    },
    onManual() {
      this.showManual();
    },
    onManualBack() {
      this.showQr();
    },
    /**
     * @param {string} deviceName
     * @param {string} username
     * @param {string} password
     */
    onManualSubmit(deviceName, username, password) {
      new Promise((resolve) => {
        // Nový pokus, resetuje se zpoždění.
        this.failureRetryDelay = config.setup.failureDelayBase;
        // Zadané údaje se uloží pro případné opakované pokusy.
        this.deviceName = deviceName;
        this.password = password;
        this.username = username;
        this.showLoader();
        return resolve(
          this.registerManualSimple(
            this.serverUrl,
            username,
            password,
            deviceName
          )
        );
      }).catch((error) => this.showFailure(error));
    },
    /**
     * @param {boolean} online
     */
    onOnlineChanged(online) {
      this.isOnline = online;
    },
    onOrganizationBack() {
      this.showManual();
    },
    /**
     * @param {Organization} organization
     */
    onOrganizationSubmit(organization) {
      new Promise((resolve) => {
        // Nový pokus, resetuje se zpoždění.
        this.failureRetryDelay = config.setup.failureDelayBase;
        // Vybraná organizace se uloží pro případné opakované pokusy.
        this.organization = organization;
        this.showLoader();
        return resolve(
          this.registerManualComplete(
            this.serverUrl,
            this.username,
            this.password,
            organization.id,
            this.deviceName
          )
        );
      }).catch((error) => this.showFailure(error));
    },
    /**
     * @param {ServerInfo} connected
     */
    onServerConnectionChanged({ connected }) {
      this.serverConnected = connected;
    },
    onSettings() {
      this.showSettings();
    },
    onStorage() {
      this.showStorage();
    },
    onSettingsBack() {
      this.showQr();
    },
    /**
     * @param {string} serverUrl
     */
    onSettingsSubmit(serverUrl) {
      this.serverUrl = serverUrl;
      this.showQr();
    },
    onStatusInfo() {
      $root(this).statusInfoDialogController.open();
    },
    onStorageBack() {
      this.showQr();
    },
    /**
     * @param {string} storage
     */
    onStorageSubmit(storage) {
      this.storage = storage;
      this.showQr();
    },
    /**
     * @param {VirtualKeyboardInfo} info
     */
    onVirtualKeyboardChanged(info) {
      this.rootStyle = this.getRootStyle(info);
    },
    /**
     * @returns {Promise<void>}
     */
    registerAutomatic() {
      return new Promise((resolve) => {
        // Při synchronizaci se použije adresa serveru přítomná v okamžiku spuštění.
        // Pokud se adresa serveru změní, použije se automaticky při dalším pokusu.
        const { serverUrl, fingerprint } = this;
        return resolve(
          SetupManager.registerAutomatic(serverUrl, fingerprint).then(
            (deviceId) => {
              if (isNil(deviceId)) {
                // Pokud zařízení nebylo nalezeno, nic neděláme.
                return;
              }
              return this.install(deviceId, serverUrl, fingerprint);
            }
          )
        );
      });
    },
    /**
     * @param {string} serverUrl
     * @param {string} username
     * @param {string} password
     * @param {string} deviceName
     * @returns {Promise<void>}
     */
    registerManualSimple(serverUrl, username, password, deviceName) {
      return new Promise((resolve) =>
        resolve(
          // Odešleme na server přihlašovací údaje a zjistíme seznam organizací.
          SetupManager.fetchOrganizations(serverUrl, username, password).then(
            (organizationArray) => {
              // Zjištěný seznam se uloží pro případné opakované pokusy.
              this.organizationArray = organizationArray.toSorted((a, b) =>
                StringUtil.compare(a.name, b.name)
              );
              // Bez organizací se dále nedá nic dělat.
              if (!organizationArray.length) {
                throw new Error('No organizations available.');
              }
              // Pokud máme na výběr pouze z jedné organizace, vyberem ji bez ptaní.
              if (organizationArray.length === 1) {
                const organization = organizationArray[0];
                // Zjištěná organizace se uloží pro případné opakované pokusy.
                this.organization = organization;
                return this.registerManualComplete(
                  serverUrl,
                  username,
                  password,
                  organization.id,
                  deviceName
                );
              }
              // Pokud je organizací více, zobrazíme jejich seznam.
              this.showOrganization();
            }
          )
        )
      );
    },
    /**
     * @param {string} serverUrl
     * @param {string} username
     * @param {string} password
     * @param {number} organizationId
     * @param {string} deviceName
     * @returns {Promise<void>}
     */
    registerManualComplete(
      serverUrl,
      username,
      password,
      organizationId,
      deviceName
    ) {
      const { fingerprint } = this;
      return new Promise((resolve) =>
        resolve(
          // Odešleme na server přihlašovací údaje a vybranou organizaci a získáme
          // ID zařízení.
          SetupManager.registerManual(
            serverUrl,
            fingerprint,
            username,
            password,
            deviceName,
            organizationId
          ).then((deviceId) => {
            // Zjištěná hodnota deviceId se uloží pro případné opakované pokusy.
            this.deviceId = deviceId;
            return this.install(deviceId, serverUrl, fingerprint);
          })
        )
      );
    },
    /**
     * @param {Error} error
     * @returns {void}
     */
    showFailure(error) {
      this.failureMessage = error.message;
      this.failureRetryTime =
        Date.now() + (this.failureRetryDelay ?? config.setup.failureDelayBase);
      this.page = 'failure';
      // TODO: To zřejmě nyní bude nějaký nesmysl, když z registrace se všechny typy chyb zaonačí za SetupError.
      // Ale třeba se to používá i na něco jiného, ale chce prověřit, nejlépe testy.
      if (error.name === ErrorName.SetupError) {
        // Známá registrační chyba se považuje za ošetřenou jejím zobrazením v GUI.
        return;
      }
      // Neznámé chyby se logují na Sentry.
      this.handleError(error);
    },
    showLoader() {
      this.page = 'loader';
    },
    showManual() {
      this.stopAutomatic();
      this.page = 'manual';
    },
    showOrganization() {
      this.page = 'organization';
    },
    showQr() {
      this.startAutomatic();
      this.page = 'qr';
    },
    showSettings() {
      this.page = 'settings';
    },
    showStorage() {
      this.page = 'storage';
    },
    showSuccess() {
      this.stopAutomatic();
      this.successTime = Date.now() + config.setup.successDelay;
      this.page = 'success';
    },
    startAutomatic() {
      // Jestli již synchronizace probíhá, tak se nic nedělá.
      if (this.automaticEnabled) {
        return;
      }
      // Jinak se ihned provede první synchronizace.
      this.automaticEnabled = true;
      this.onAutomatic();
    },
    stopAutomatic() {
      clearInterval(this.automaticTimeout);
      this.automaticTimeout = undefined;
      this.automaticEnabled = false;
    }
  },
  props: {},
  watch: {
    /**
     * @param {string} newServerUrl
     */
    serverUrl(newServerUrl) {
      const { serverConnectionMonitor } = $root(this);
      // Aktualizuje se sledování připojení k serveru.
      serverConnectionMonitor.setServerUrl(newServerUrl);
    }
  }
});
